import CloseIcon from '@mui/icons-material/Close'
import { Box, IconButton, Tab, Tabs, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import EventsEngine from '../../engines/EventsEngine'
import Helper from '../../helpers/Helper'
import FlowInitializer from './FlowInitializer'

export interface IFlowTab {
    flowId: string
    name: string
}

interface P {
    mainFlowId: string
}

function NestedFlows({ mainFlowId }: P) {
    const theme = useTheme()
    const [tabs, setTabs] = useState<IFlowTab[]>([])
    const [selectedTab, setSelectedTab] = useState<IFlowTab>(null)
    const [disableSwitch, setDisableSwitch] = useState(false)

    useEffect(() => {
        const uuid = Helper.generateUUID()

        EventsEngine.subscribe('add-flow-tab', 'flow-tabs' + uuid, (flowTab: IFlowTab) => {
            if (tabs.some(x => x?.flowId === flowTab?.flowId)) {
                setSelectedTab(flowTab)
            } else {
                setTabs([...tabs, flowTab])
                setSelectedTab(flowTab)
            }
        })

        EventsEngine.subscribe('disable-switch-tab-flow', 'flow-tabs' + uuid, (state: boolean) => {
            setDisableSwitch(state)
        })

        return () => {
            EventsEngine.unsubscribe('add-flow-tab', 'flow-tabs' + uuid)
            EventsEngine.unsubscribe('disable-switch-tab-flow', 'flow-tabs' + uuid)
        }
    }, [tabs])

    useEffect(() => {
        const mainFlow = { flowId: mainFlowId, name: 'Main Flow' }
        setTabs([mainFlow])
        setSelectedTab(mainFlow)
    }, [mainFlowId])

    const onClose = (tabToClose: IFlowTab) => {
        if (selectedTab.flowId === mainFlowId) return
        const newTabs = tabs.filter(x => x.flowId !== tabToClose.flowId)
        setTabs(newTabs)

        if (selectedTab.flowId === tabToClose.flowId) {
            const indexTab = tabs.findIndex(x => x.flowId === tabToClose.flowId)
            if (indexTab > 0) setSelectedTab(tabs[indexTab - 1])
            else setSelectedTab(newTabs[newTabs.length - 1])
        }
    }

    const renderFlowTabs = () => {
        if (tabs.length <= 1) return null

        return (
            <Box sx={{ backgroundColor: theme.palette.background.default }} borderRadius={3} position='absolute' zIndex={1} bottom={10} left={10}>
                <Tabs
                    value={selectedTab?.flowId}
                    onChange={(_, value) => setSelectedTab(tabs.find(tab => tab.flowId === value))}
                    variant='scrollable'
                    TabIndicatorProps={{ children: <span className='MuiTabs-indicatorSpan' /> }}
                    sx={{
                        '&.MuiTabs-root': {
                            maxWidth: 'calc(var(--unit) * 45)',
                            display: 'flex'
                        },
                        '& .MuiTabs-scroller': {
                            display: 'flex',
                            alignItems: 'center',
                            flex: 1,
                            paddingBottom: 1
                        },
                        '& .MuiTabs-indicator': {
                            display: 'flex',
                            justifyContent: 'center',
                            backgroundColor: 'transparent'
                        },
                        '& .MuiTabs-indicatorSpan': {
                            maxWidth: '80%',
                            width: '100%',
                            backgroundColor: theme.palette.subFlow.main,
                            borderRadius: '50%'
                        }
                    }}>
                    {tabs.map(tab => (
                        <Tab
                            key={tab.flowId}
                            label={tab.name}
                            disabled={disableSwitch}
                            value={tab.flowId}
                            icon={(tab?.flowId !== mainFlowId && selectedTab.flowId === tab.flowId) && (
                                <IconButton onClick={() => onClose(tab)} sx={{ fontSize: '10px', padding: 0, margin: 0 }}>
                                    <CloseIcon fontSize='small' />
                                </IconButton>
                            )}
                            iconPosition='end'
                            sx={{
                                '&.MuiTab-root': { minHeight: 0, paddingBottom: 0 },
                                '&.Mui-selected': { color: 'var(--orange-A700)' }
                            }}
                        />
                    ))}
                </Tabs>
            </Box>
        )
    }

    const renderSubFlow = () => {
        if (selectedTab?.flowId === mainFlowId) return null

        return (
            <Box flex={1} position='absolute' display='flex' width='calc(100vw - var(--left-nav-width))' height='calc(100vh - var(--top-bar-height))' sx={{ backgroundColor: theme.palette.background.default }} top={0} left={0}>
                {selectedTab && <FlowInitializer flowId={selectedTab?.flowId} />}
            </Box>
        )
    }

    return (
        <Box>
            {renderFlowTabs()}
            {renderSubFlow()}
        </Box>
    )
}

export default NestedFlows
